import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, ShieldCheck, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDeck, useDeckMissingCards, useCreateCheckout } from '@/hooks/useApi';
import CardImage from '@/components/CardImage';

interface MissingCard {
  card_id: string;
  card_name: string;
  image_url?: string;
  needed: number;
  owned: number;
  missing_count: number;
  category: string;
  game: string;
  market_price?: number;
  cheapest_listing?: {
    id: string;
    price: number;
    seller_username: string;
    condition: string;
  } | null;
}

export default function MissingCardsPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate   = useNavigate();

  const { data: deck }    = useDeck(deckId!);
  const { data: missingData, isLoading } = useDeckMissingCards(deckId!);
  const { mutate: checkout, isLoading: checkingOut } = useCreateCheckout();

  const [buyingListingId, setBuyingListingId] = useState<string | null>(null);

  const cards: MissingCard[] = (missingData as any)?.missing ?? [];
  const totalEstimated: number = (missingData as any)?.total_estimated_cost ?? 0;

  const handleBuyNow = (listingId: string) => {
    setBuyingListingId(listingId);
    checkout(listingId, {
      onSuccess: (data: any) => {
        if (data?.url) window.location.href = data.url;
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error?.message ?? 'Checkout failed');
        setBuyingListingId(null);
      },
    });
  };

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
        <div className="container-custom py-4 flex items-center gap-4">
          <button onClick={() => navigate(`/collection/decks/${deckId}`)} className="text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              Missing Cards
            </h1>
            {deck && (
              <p className="text-sm text-gray-500 dark:text-zinc-500">for {deck.name}</p>
            )}
          </div>
          <Link to={`/collection/decks/${deckId}`} className="btn-outline py-1.5 text-sm">
            Back to Builder
          </Link>
        </div>
      </div>

      <div className="container-custom py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-brand-400" />
          </div>
        ) : cards.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-gray-200 dark:border-zinc-800 p-16 text-center">
            <ShieldCheck className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 dark:text-zinc-200 mb-2">You own all the cards!</h2>
            <p className="text-gray-500 dark:text-zinc-500 text-sm mb-6">Your collection is complete for this deck.</p>
            <Link to={`/collection/decks/${deckId}`} className="btn-primary px-6 py-2 inline-block">
              Back to Builder
            </Link>
          </div>
        ) : (
          <>
            {/* Summary banner */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  {cards.length} card type{cards.length !== 1 ? 's' : ''} missing
                  &nbsp;·&nbsp;
                  {cards.reduce((s, c) => s + c.missing_count, 0)} total copies needed
                </p>
                {totalEstimated > 0 && (
                  <p className="text-sm text-amber-600 mt-0.5">
                    Estimated cost via marketplace: <strong>${totalEstimated.toFixed(2)}</strong>
                  </p>
                )}
              </div>
              <Link to="/marketplace" className="btn-outline border-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:bg-amber-900/40 py-1.5 text-sm whitespace-nowrap">
                Browse Marketplace
              </Link>
            </div>

            {/* Cards table */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-zinc-800 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-500 dark:text-zinc-500 text-xs uppercase tracking-wide">Card</th>
                    <th className="px-4 py-3 font-semibold text-gray-500 dark:text-zinc-500 text-xs uppercase tracking-wide text-center">Need / Own</th>
                    <th className="px-4 py-3 font-semibold text-gray-500 dark:text-zinc-500 text-xs uppercase tracking-wide hidden md:table-cell">Category</th>
                    <th className="px-4 py-3 font-semibold text-gray-500 dark:text-zinc-500 text-xs uppercase tracking-wide">Cheapest Listing</th>
                    <th className="px-4 py-3 font-semibold text-gray-500 dark:text-zinc-500 text-xs uppercase tracking-wide text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {cards.map(card => (
                    <tr key={card.card_id} className="hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors">
                      {/* Card info */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-14 rounded-lg overflow-hidden flex-shrink-0">
                            <CardImage
                              src={undefined}
                              alt={card.card_name}
                              cardName={card.card_name}
                              className="w-full h-full"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-zinc-100">{card.card_name}</p>
                            {card.market_price != null && (
                              <p className="text-xs text-gray-500 dark:text-zinc-500">Market: ${Number(card.market_price).toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Qty */}
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-red-500 font-bold">{card.missing_count}</span>
                          <span className="text-gray-500 dark:text-zinc-500 text-xs">missing</span>
                        </span>
                        <p className="text-xs text-gray-500 dark:text-zinc-500">{card.owned}/{card.needed}</p>
                      </td>

                      {/* Category */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 rounded-full">{card.category}</span>
                      </td>

                      {/* Cheapest listing */}
                      <td className="px-4 py-3">
                        {card.cheapest_listing ? (
                          <div>
                            <p className="font-bold text-gray-900 dark:text-zinc-100">${Number(card.cheapest_listing.price).toFixed(2)}</p>
                            <p className="text-xs text-gray-500 dark:text-zinc-500">
                              {card.cheapest_listing.condition} · @{card.cheapest_listing.seller_username}
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-gray-500 dark:text-zinc-500">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span className="text-xs">No listings</span>
                          </div>
                        )}
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3 text-right">
                        {card.cheapest_listing ? (
                          <button
                            onClick={() => handleBuyNow(card.cheapest_listing!.id)}
                            disabled={checkingOut}
                            className="btn-primary py-1.5 px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                          >
                            {buyingListingId === card.cheapest_listing.id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <ShoppingBag className="h-3 w-3" />
                            }
                            Buy Now
                          </button>
                        ) : (
                          <Link
                            to={`/marketplace?search=${encodeURIComponent(card.card_name)}`}
                            className="btn-outline py-1.5 px-3 text-xs inline-flex items-center gap-1.5"
                          >
                            <ExternalLink className="h-3 w-3" /> Search
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
